<template>
  <UmisisModal
    ref="titleModal"
    :ok-disabled="isUpdating || !draftTitle.trim()"
    title="Update Template Title"
    @ok="submitTitle"
    @show="onModalShow"
  >
    <Field>
      <FieldLabel for="task-template-title-input">
        Title
      </FieldLabel>
      <Input
        id="task-template-title-input"
        v-model="draftTitle"
        type="text"
        maxlength="255"
        placeholder="Enter template title"
      />
      <FieldDescription>Set the default title for generated tasks.</FieldDescription>
    </Field>
  </UmisisModal>
</template>

<script setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import UmisisModal from '../../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../../resources/js/components/ui/field/index.js'
import { Input } from '../../../../../../../../resources/js/components/ui/input/index.js'
import { useTaskTemplateStore } from '../../../stores/taskTemplateStore.js'

const props = defineProps({
  taskTemplateId: {
    type: [Number, String],
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  context: {
    type: String,
    default: 'task-template-show',
  },
})

const draftTitle = ref('')
const { componentRef: titleModal, openModal: openDialog, closeModal: closeDialog } = useModalControl()

const taskTemplateStore = useTaskTemplateStore(props.context)
const normalizedTaskTemplateId = computed(() => Number(props.taskTemplateId))
const isUpdating = computed(() => taskTemplateStore.isUpdatingTitle(normalizedTaskTemplateId.value))

const openModal = () => {
  openDialog()
}

const closeModal = () => {
  closeDialog()
}

const onModalShow = () => {
  draftTitle.value = props.title ?? ''
}

const submitTitle = async (event) => {
  event?.preventDefault?.()

  if (!Number.isInteger(normalizedTaskTemplateId.value) || normalizedTaskTemplateId.value <= 0) {
    return
  }

  const nextTitle = draftTitle.value.trim()

  if (nextTitle === '') {
    toast.error('Title is required.')
    return
  }

  if (nextTitle === props.title) {
    closeDialog()
    return
  }

  try {
    await taskTemplateStore.updateTitle(normalizedTaskTemplateId.value, nextTitle)
    toast.success('Template title updated.')
    closeDialog()
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.title?.[0]
      || 'Unable to update template title.'

    toast.error(errorMessage)
  }
}

defineExpose({
  openModal,
  closeModal,
})
</script>
